import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { Subscription, SubscriptionUsageDto } from '../types';
import { subscriptionsApi, customerPortalApi } from '../services/api';
import { useAuth } from './AuthContext';
import { getErrorMessage, isAxiosError } from '../utils/errorUtils';

interface SubscriptionState {
  currentSubscription: Subscription | null;
  usage: SubscriptionUsageDto | null;
  isLoading: boolean;
  error: string | null;
}

type SubscriptionAction =
  | { type: 'SUBSCRIPTION_START' }
  | { type: 'SUBSCRIPTION_SUCCESS'; payload: { subscription: Subscription; usage: SubscriptionUsageDto } }
  | { type: 'SUBSCRIPTION_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_USAGE'; payload: SubscriptionUsageDto };

const initialState: SubscriptionState = {
  currentSubscription: null,
  usage: null,
  isLoading: false,
  error: null,
};

const subscriptionReducer = (state: SubscriptionState, action: SubscriptionAction): SubscriptionState => {
  switch (action.type) {
    case 'SUBSCRIPTION_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'SUBSCRIPTION_SUCCESS':
      return {
        ...state,
        currentSubscription: action.payload.subscription,
        usage: action.payload.usage,
        isLoading: false,
        error: null,
      };
    case 'SUBSCRIPTION_ERROR':
      return {
        ...state,
        currentSubscription: null,
        usage: null,
        isLoading: false,
        error: action.payload,
      };
    case 'UPDATE_USAGE':
      return {
        ...state,
        usage: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

interface SubscriptionContextType {
  state: SubscriptionState;
  loadSubscription: () => Promise<void>;
  createCheckoutSession: (tier: string, successUrl: string, cancelUrl: string) => Promise<string>;
  cancelSubscription: (subscriptionId: number) => Promise<void>;
  createCustomerPortal: (returnUrl: string) => Promise<string>;
  canUseService: () => Promise<boolean>;
  clearError: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: React.ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(subscriptionReducer, initialState);
  const { state: authState } = useAuth();

  const loadSubscription = useCallback(async (): Promise<void> => {
    if (!authState.isAuthenticated) {
      return;
    }

    try {
      dispatch({ type: 'SUBSCRIPTION_START' });
      
      const [subscriptionResponse, usageResponse] = await Promise.all([
        subscriptionsApi.getCurrent(),
        subscriptionsApi.getUsage()
      ]);
      
      dispatch({ 
        type: 'SUBSCRIPTION_SUCCESS', 
        payload: { 
          subscription: subscriptionResponse, 
          usage: usageResponse 
        }
      });
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to load subscription');
      dispatch({ type: 'SUBSCRIPTION_ERROR', payload: message });
    }
  }, [authState.isAuthenticated]);

  const createCheckoutSession = async (tier: string, successUrl: string, cancelUrl: string): Promise<string> => {
    try {
      const response = await subscriptionsApi.createCheckoutSession({
        tier,
        successUrl,
        cancelUrl,
      });
      return response.url;
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to create checkout session');
      dispatch({ type: 'SUBSCRIPTION_ERROR', payload: message });
      throw error;
    }
  };

  const cancelSubscription = async (subscriptionId: number): Promise<void> => {
    try {
      await subscriptionsApi.cancel(subscriptionId);
      await loadSubscription(); // Reload subscription data
    } catch (error) {
      const message = getErrorMessage(error, 'Failed to cancel subscription');
      dispatch({ type: 'SUBSCRIPTION_ERROR', payload: message });
      throw error;
    }
  };

  const createCustomerPortal = async (returnUrl: string): Promise<string> => {
    try {
      const response = await customerPortalApi.createSession(returnUrl);
      return response.url;
    } catch (error) {
      // 404 means the user has no Stripe billing account yet — let the caller
      // present a friendly inline message instead of the global error banner.
      if (isAxiosError(error) && error.response?.status === 404) {
        throw error;
      }
      const message = getErrorMessage(error, 'Failed to create portal session');
      dispatch({ type: 'SUBSCRIPTION_ERROR', payload: message });
      throw error;
    }
  };

  const canUseService = async (): Promise<boolean> => {
    try {
      const response = await subscriptionsApi.getUsage();
      return response.remainingCount > 0 || response.remainingCount === -1; // -1 means unlimited
    } catch (error) {
      return false;
    }
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Load subscription data when user authenticates
  useEffect(() => {
    if (authState.isAuthenticated) {
      loadSubscription();
    }
  }, [authState.isAuthenticated, loadSubscription]);

  const value: SubscriptionContextType = {
    state,
    loadSubscription,
    createCheckoutSession,
    cancelSubscription,
    createCustomerPortal,
    canUseService,
    clearError,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};